import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PrismLoading } from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';
import './ReminderSettingsTab.css';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'every_2_days', label: 'Every 2 Days' },
  { value: 'every_3_days', label: 'Every 3 Days' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'business_days_only', label: 'Business Days Only' },
];

const ReminderSettingsTab = ({ currentUser }) => {
  const [chapters, setChapters] = useState([]);
  const [configs, setConfigs] = useState({});
  const [windows, setWindows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [sending, setSending] = useState({});

  const isAdmin = currentUser?.Role?.permission_level >= 100;
  const userChapterId = currentUser?.chapter_id;
  const canEditChapter = (chapterId) => isAdmin || chapterId === userChapterId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chaptersRes, configsRes, windowsRes] = await Promise.all([
          axios.get('/api/chapters'),
          axios.get('/api/manager_approval_reminder_config'),
          axios.get('/api/application_windows/all'),
        ]);
        setChapters(chaptersRes.data || []);
        const configMap = {};
        (configsRes.data || []).forEach(c => { configMap[c.chapter_id] = c; });
        setConfigs(configMap);
        const windowMap = {};
        (windowsRes.data || []).forEach(w => { windowMap[w.chapter_id] = w; });
        setWindows(windowMap);
      } catch (err) {
        console.error('Error fetching reminder settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async (chapterId) => {
    setSaving(prev => ({ ...prev, [chapterId]: true }));
    const config = configs[chapterId] || {};
    try {
      const res = await axios.put(`/api/manager_approval_reminder_config/${chapterId}`, {
        enabled: config.enabled || false,
        frequency: config.frequency || 'weekly',
        start_after_submission_days: config.start_after_submission_days ?? 3,
        send_until_days_before_close: config.send_until_days_before_close ?? 1,
        from_address: config.from_address || null,
      });
      setConfigs(prev => ({ ...prev, [chapterId]: res.data }));
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(prev => ({ ...prev, [chapterId]: false }));
    }
  };

  const handleSendNow = async (chapterId) => {
    setSending(prev => ({ ...prev, [chapterId]: true }));
    try {
      const res = await axios.post(`/api/manager_approval_reminder_config/${chapterId}/send_now`);
      const { sent, failed } = res.data;
      if (failed === 0) {
        toast.success(`${sent} reminder email(s) sent.`);
      } else if (sent === 0) {
        toast.error('Reminder send failed — no emails were delivered.');
      } else {
        toast.warn(`${sent} reminder email(s) sent. ${failed} failed to deliver — check server logs or retry.`);
      }
    } catch (err) {
      toast.error('Failed to send reminders');
    } finally {
      setSending(prev => ({ ...prev, [chapterId]: false }));
    }
  };

  const updateConfig = (chapterId, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [chapterId]: { ...(prev[chapterId] || {}), chapter_id: chapterId, [field]: value },
    }));
  };

  if (loading) return <PrismLoading as="spinner" container="inline" />;

  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d;
  };

  return (
    <div className="manager-approval-card-grid">
      {chapters.map(chapter => {
        const config = configs[chapter.id] || { enabled: false, frequency: 'weekly', start_after_submission_days: 3, send_until_days_before_close: 1 };
        const isSaving = saving[chapter.id];
        const isSending = sending[chapter.id];
        const appWindow = windows[chapter.id];
        const softDeadline = appWindow?.soft_deadline;
        const startDays = config.start_after_submission_days ?? 3;
        const stopDays = config.send_until_days_before_close ?? 1;
        const stopDate = softDeadline ? addDays(softDeadline, -stopDays) : null;
        const canEdit = canEditChapter(chapter.id);

        return (
          <div key={chapter.id} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '16px', marginBottom: '16px', opacity: canEdit ? 1 : 0.7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0 }}>{chapter.location}</h4>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={config.enabled || false}
                  onChange={(e) => updateConfig(chapter.id, 'enabled', e.target.checked)}
                  disabled={!canEdit}
                />
                Enabled
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '12px' }}>
              <label style={{ flex: '0 0 200px' }}>
                Frequency:
                <select
                  className="reminder-settings-select"
                  value={config.frequency || 'weekly'}
                  onChange={(e) => updateConfig(chapter.id, 'frequency', e.target.value)}
                  disabled={!canEdit}
                  style={{ display: 'block', width: '100%', marginTop: '4px', padding: '4px' }}
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <button
                  onClick={() => handleSave(chapter.id)}
                  disabled={isSaving || !canEdit}
                  style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', backgroundColor: 'var(--color-primary)', color: '#fff', cursor: canEdit ? 'pointer' : 'not-allowed' }}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => handleSendNow(chapter.id)}
                  disabled={isSending || !canEdit}
                  style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: canEdit ? 'pointer' : 'not-allowed' }}
                >
                  {isSending ? 'Sending...' : 'Send Reminder Now'}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              <label>
                Start after (days):
                <input
                  className="reminder-settings-number"
                  type="number"
                  min={0}
                  value={startDays}
                  onChange={(e) => updateConfig(chapter.id, 'start_after_submission_days', Number(e.target.value))}
                  disabled={!canEdit}
                  style={{ display: 'block', width: '100%', marginTop: '4px', padding: '4px' }}
                />
                <span style={{ fontSize: '0.8em', color: 'var(--color-text-faded)', marginTop: '2px', display: 'block' }}>
                  First reminder {startDays} day{startDays !== 1 ? 's' : ''} after each submission
                </span>
              </label>
              <label>
                Stop before close (days):
                <input
                  className="reminder-settings-number"
                  type="number"
                  min={0}
                  value={stopDays}
                  onChange={(e) => updateConfig(chapter.id, 'send_until_days_before_close', Number(e.target.value))}
                  disabled={!canEdit}
                  style={{ display: 'block', width: '100%', marginTop: '4px', padding: '4px' }}
                />
                <span style={{ fontSize: '0.8em', color: 'var(--color-text-faded)', marginTop: '2px', display: 'block' }}>
                  {softDeadline
                    ? <>Soft deadline: <strong>{formatDate(new Date(softDeadline))}</strong> → last reminder: <strong>{formatDate(stopDate)}</strong></>
                    : <em>No soft deadline set for this chapter</em>}
                </span>
              </label>
            </div>

            {/* Last run status */}
            {config.last_run_at && (
              <p style={{ fontSize: '0.85em', color: config.last_run_failed_count > 0 ? 'var(--color-danger-dk)' : 'var(--color-text-faded)', marginBottom: '8px' }}>
                Last run: {new Date(config.last_run_at).toLocaleString()} — {config.last_run_sent_count} sent, {config.last_run_failed_count} failed
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReminderSettingsTab;
